import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
// AnalyticsTab pulls in recharts (~250 kB). Most users on this page
// never open the analytics tab — lazy-loading keeps that cost off the
// initial bundle and only fetches it when the user actually clicks
// the tab.
const AnalyticsTab = lazy(() => import('./AnalyticsTab'));
import MandatesTab from './MandatesTab';
import firmService from '../services/firmService';
import FirmCandidatesTab from './FirmCandidatesTab';
import ColumnBasedLayout from './ColumnBasedLayout';
import FirmAboutWidget from './widgets/firm/FirmAboutWidget';
import FirmKeyFactsWidget from './widgets/firm/FirmKeyFactsWidget';
import FirmOfficesWidget from './widgets/firm/FirmOfficesWidget';
import FirmEnrichmentWidget from './widgets/firm/FirmEnrichmentWidget';
import FirmNotesWidget from './widgets/firm/FirmNotesWidget';
import FirmNotesLogWidget from './widgets/firm/FirmNotesLogWidget';
import FirmPipelinesWidget from './widgets/firm/FirmPipelinesWidget';
import FirmDocumentsWidget from './widgets/firm/FirmDocumentsWidget';
import FirmTechStackWidget from './widgets/firm/FirmTechStackWidget';

import EditableField from './EditableField';
import FirmTagBar from './FirmTagBar';

/**
 * FirmDetail — the firm profile + tabs, usable in two modes:
 *   • full-page (route): renders inside the /firm/:id page (embedded=false)
 *   • embedded (master-detail pane): renders inside the /firms rail layout
 *
 * Props:
 *   firmId       — id of the firm to load (required)
 *   embedded     — when true, strips page chrome (max-width centering) and
 *                  swaps the back button for an "Expand ↗" affordance
 *   onExpand     — embedded only: called when the user clicks "Expand ↗"
 *                  (typically navigates to /firm/:id)
 *   onClose      — embedded only: called by the header X to deselect
 *   onDeleted    — called after a successful delete (parent can refresh/clear)
 */
export default function FirmDetail({ firmId, embedded = false, onExpand, onClose, onDeleted }) {
    const navigate = useNavigate();
    const [firm, setFirm] = useState(null);
    const logoInputRef = React.useRef(null);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Please choose an image file.'); return; }
        if (file.size > 2 * 1024 * 1024) { alert('Please choose an image under 2MB.'); return; }
        const reader = new FileReader();
        reader.onload = () => { patchField({ logoData: reader.result }); };
        reader.onerror = () => alert('Could not read the file.');
        reader.readAsDataURL(file);
        e.target.value = '';
    };
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [syncingLinkedIn, setSyncingLinkedIn] = useState(false);

    const token = localStorage.getItem('authToken');
    const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchFirmDetail(); setActiveTab('overview'); /* eslint-disable-next-line */ }, [firmId]);

    const fetchFirmDetail = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(`${API_URL}/firms/${firmId}/detailed`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('Failed to fetch firm details');
            setFirm(await res.json());
        } catch (err) { setError(err.message); }
        finally { setLoading(false); }
    };

    // Inline edit: patch one or more fields with an optimistic update + rollback.
    // `patch` is a partial firm object (e.g. { aum: '2.5B' }).
    const [savingField, setSavingField] = useState(null);
    const patchField = async (patch) => {
        const prev = firm;
        const next = { ...firm, ...patch };
        setFirm(next); // optimistic
        setSavingField(Object.keys(patch)[0]);
        try {
            await firmService.updateFirm(firmId, next);
        } catch (error) {
            setFirm(prev); // rollback
            alert('Failed to save: ' + error.message);
        } finally {
            setSavingField(null);
        }
    };

    const handleDeleteFirm = async (id) => {
        try {
            await firmService.deleteFirm(id);
            if (onDeleted) onDeleted(id);
            else navigate('/firms');
        }
        catch (error) { alert('Failed to delete firm: ' + error.message); }
    };

    const handleSyncLinkedIn = async (force = false) => {
        let linkedinUrlToUse = firm.linkedinUrl;

        if (!linkedinUrlToUse) {
            const url = window.prompt('Enter the LinkedIn company URL for this firm:', '');
            if (!url) return;
            linkedinUrlToUse = url;
        }

        // Ask about overwrite behavior (skip on force sync — assume same as last)
        let shouldOverwrite = false;
        if (!force) {
            shouldOverwrite = window.confirm(
                'Sync from LinkedIn?\n\n' +
                'OK = Overwrite all fields with LinkedIn data (replaces offices too)\n' +
                'Cancel = Fill only blank fields (keep existing data)'
            );
        }

        setSyncingLinkedIn(true);
        try {
            // Step 1: Trigger the async scrape
            const triggerBody = { force };
            if (!firm.linkedinUrl) triggerBody.linkedin_url = linkedinUrlToUse;

            const triggerRes = await fetch(`${API_URL}/firms/${firmId}/sync-linkedin-trigger`, {
                method: 'POST', headers: authHeaders,
                body: JSON.stringify(triggerBody)
            });
            const triggerData = await triggerRes.json();

            // Cooldown blocked — offer force option
            if (triggerRes.status === 429 && triggerData.cooldown) {
                const lastSyncDate = triggerData.last_synced_at
                    ? new Date(triggerData.last_synced_at).toLocaleDateString()
                    : 'unknown date';
                const proceed = window.confirm(
                    `Sync blocked by cooldown.\n\n` +
                    `This firm was last synced ${triggerData.days_since_sync} days ago (${lastSyncDate}).\n` +
                    `Org cooldown is set to ${triggerData.cooldown_days} days.\n` +
                    `${triggerData.days_remaining} days remaining until next allowed sync.\n\n` +
                    `Force sync now anyway?`
                );
                setSyncingLinkedIn(false);
                if (proceed) {
                    return handleSyncLinkedIn(true);
                }
                return;
            }

            if (!triggerRes.ok) throw new Error(triggerData.detail || triggerData.error || 'Failed to start sync');

            const { snapshot_id, url: syncedUrl } = triggerData;

            // Step 2: Poll for completion (every 5s, up to 8 minutes)
            const maxAttempts = 96;
            const pollInterval = 5000;
            let result = null;

            for (let i = 0; i < maxAttempts; i++) {
                await new Promise(r => setTimeout(r, pollInterval));

                const params = new URLSearchParams({
                    snapshot_id,
                    overwrite: String(shouldOverwrite),
                    url: syncedUrl
                });
                const statusRes = await fetch(`${API_URL}/firms/${firmId}/sync-linkedin-status?${params}`, {
                    headers: authHeaders
                });
                const statusData = await statusRes.json();

                if (statusData.status === 'ready') {
                    result = statusData;
                    break;
                }
                if (statusData.status === 'failed') {
                    throw new Error(statusData.error || statusData.detail || 'Sync failed');
                }
            }

            if (!result) {
                throw new Error('Sync timed out after 8 minutes. The scrape may still complete in the background — try refreshing in a few minutes.');
            }

            const synced = result.synced || {};
            const summary = Object.entries(synced).filter(([k, v]) => v).map(([k]) => k).join(', ');
            alert(`Synced from LinkedIn successfully${summary ? '\n\nUpdated: ' + summary : ''}`);
            fetchFirmDetail();
        } catch (err) { alert('LinkedIn sync failed: ' + err.message); }
        finally { setSyncingLinkedIn(false); }
    };

    const tabs = ['overview', 'analytics', 'mandates', 'candidates'];

    // Overview widget grid (movable/hideable via ColumnBasedLayout).
    const firmWidgets = {
        'firm-about': FirmAboutWidget,
        'firm-key-facts': FirmKeyFactsWidget,
        'firm-offices': FirmOfficesWidget,
        'firm-enrichment': FirmEnrichmentWidget,
        'firm-notes': FirmNotesWidget,
        'firm-notes-log': FirmNotesLogWidget,
        'firm-pipelines': FirmPipelinesWidget,
        'firm-documents': FirmDocumentsWidget,
        'firm-tech-stack': FirmTechStackWidget,
    };
    const widgetProps = { firm, firmId, onRefresh: fetchFirmDetail };
    const [layoutEditState, setLayoutEditState] = useState({});
    const isLayoutEdit = layoutEditState.isEditMode;

    // Wrapper classes differ by mode: full-page owns the screen; embedded fills its pane.
    const rootClass = embedded
        ? 'h-full overflow-hidden bg-white flex flex-col'
        : 'h-full overflow-hidden bg-gray-50 flex flex-col';
    const centerClass = embedded ? 'px-6' : 'max-w-7xl mx-auto px-6';

    if (loading) return (
        <div className="h-full bg-gray-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
    );

    if (error || !firm) return (
        <div className="h-full bg-gray-50 flex items-center justify-center">
            <div className="text-center">
                <p className="text-sm text-red-600 mb-3">{error || 'Firm not found'}</p>
                {embedded
                    ? <button onClick={() => onClose?.()} className="text-xs text-emerald-600 hover:text-emerald-700">Close</button>
                    : <button onClick={() => navigate('/firms')} className="text-xs text-emerald-600 hover:text-emerald-700">← Back to Firms</button>}
            </div>
        </div>
    );

    // Info row; when `field` is provided the value becomes inline-editable.
    return (
        <div className={rootClass}>

            {/* Firm Header */}
            <div className="bg-white border-b flex-shrink-0">
                <div className={`${centerClass} py-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {embedded ? (
                                <button onClick={() => onClose?.()} title="Close" className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            ) : (
                                <button onClick={() => navigate('/firms')} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                            )}
                            {(firm.logoData || firm.logoUrl) ? (
                                <div className="relative group/logo">
                                    <img src={firm.logoData || firm.logoUrl} alt={firm.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.nextSibling.style.display = 'flex'; }} />
                                    <button onClick={() => logoInputRef.current?.click()} title="Change logo"
                                        className="absolute inset-0 rounded-lg bg-black/50 opacity-0 group-hover/logo:opacity-100 flex items-center justify-center transition-opacity">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </button>
                                </div>
                            ) : null}
                            <button onClick={() => logoInputRef.current?.click()} title="Upload logo"
                                className={`w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-sm font-bold items-center justify-center hover:opacity-90 ${(firm.logoData || firm.logoUrl) ? 'hidden' : 'flex'}`}>
                                {firm.name?.substring(0, 2).toUpperCase()}
                            </button>
                            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                            <div>
                                <h1 className="text-lg font-bold text-gray-900">
                                    <EditableField value={firm.name} saving={savingField === 'name'} onSave={(v) => patchField({ name: v })} inputClassName="text-lg font-bold w-64" className="text-lg font-bold" />
                                </h1>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {firm.founded && <span className="text-xs text-gray-500">Founded {firm.founded}</span>}
                                    {firm.offices?.length > 0 && (
                                        <>
                                            <span className="text-gray-300">·</span>
                                            <span className="text-xs text-gray-500">{firm.offices.find(o => o.isPrimary)?.city || firm.offices[0]?.city}</span>
                                        </>
                                    )}
                                    {firm.totalEmployees > 0 && (
                                        <>
                                            <span className="text-gray-300">·</span>
                                            <span className="text-xs text-gray-500">{firm.totalEmployees.toLocaleString()} employees</span>
                                        </>
                                    )}
                                    {firm.website && (
                                        <>
                                            <span className="text-gray-300">·</span>
                                            <a href={firm.website.startsWith('http') ? firm.website : `https://${firm.website}`} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-emerald-600 hover:text-emerald-800 hover:underline">Website</a>
                                        </>
                                    )}
                                    {firm.linkedinUrl && (
                                        <>
                                            <span className="text-gray-300">·</span>
                                            <a href={firm.linkedinUrl} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-[#0A66C2] hover:text-[#004182] hover:underline">LinkedIn</a>
                                        </>
                                    )}
                                </div>
                                {/* Firm tags — under the company name/info */}
                                <div className="mt-2">
                                    <FirmTagBar firmId={firmId} customTags={firm.custom_tags || {}} onRefresh={fetchFirmDetail} />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Layout edit toolbar — only on the Overview widget grid */}
                            {activeTab === 'overview' && (
                                isLayoutEdit ? (
                                    <>
                                        {layoutEditState.hiddenWidgetsCount > 0 && (
                                            <span className="text-[11px] text-gray-400">{layoutEditState.hiddenWidgetsCount} hidden</span>
                                        )}
                                        <button onClick={() => layoutEditState.onReset?.()}
                                            className="px-2.5 h-[30px] text-xs font-medium border border-gray-300 text-gray-500 bg-white rounded hover:bg-gray-50">
                                            Reset
                                        </button>
                                        <button onClick={() => layoutEditState.onCancel?.()}
                                            className="px-2.5 h-[30px] text-xs font-medium border border-gray-300 text-gray-600 bg-white rounded hover:bg-gray-50">
                                            Cancel
                                        </button>
                                        <button onClick={() => layoutEditState.onSave?.()} disabled={layoutEditState.isSaving || !layoutEditState.hasChanges}
                                            className="px-3 h-[30px] text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-50">
                                            {layoutEditState.isSaving ? 'Saving…' : 'Save Layout'}
                                        </button>
                                    </>
                                ) : (
                                    <button onClick={() => layoutEditState.setIsEditMode?.(true)} title="Rearrange widgets"
                                        className="px-2.5 h-[30px] text-xs font-medium border border-gray-300 text-gray-600 bg-white rounded hover:bg-gray-50 flex items-center gap-1.5">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                                        Edit Layout
                                    </button>
                                )
                            )}
                            {embedded && onExpand && !isLayoutEdit && (
                                <button onClick={() => onExpand(firm)} title="Open full page"
                                    className="px-2.5 h-[30px] text-xs font-medium border border-gray-300 text-gray-600 bg-white rounded hover:bg-gray-50 flex items-center gap-1.5">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                                    Expand
                                </button>
                            )}
                            {!isLayoutEdit && (
                              <button onClick={() => handleSyncLinkedIn(false)} disabled={syncingLinkedIn}
                                className="px-3 h-[30px] text-xs font-medium border border-[#0A66C2]/30 text-[#0A66C2] bg-[#0A66C2]/5 rounded hover:bg-[#0A66C2]/10 flex items-center gap-1.5 disabled:opacity-50">
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                {syncingLinkedIn ? 'Syncing...' : 'Sync LinkedIn'}
                              </button>
                            )}
                            {!isLayoutEdit && (
                              <button onClick={() => { if (window.confirm(`Delete ${firm.name}? This cannot be undone.`)) handleDeleteFirm(firmId); }}
                                className="px-3 h-[30px] text-xs font-medium border border-red-200 text-red-600 bg-white rounded hover:bg-red-50 flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Delete
                              </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b flex-shrink-0">
                <div className={centerClass}>
                    <nav className="flex gap-1 overflow-x-auto">
                        {tabs.map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)}
                                className={`flex-shrink-0 px-3 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                                    activeTab === tab ? 'border-emerald-600 text-emerald-700 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}>
                                {tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className={`${centerClass} py-5 w-full`}>

                {activeTab === 'overview' && (
                    <ColumnBasedLayout
                        pageType="firm_profile"
                        widgets={firmWidgets}
                        widgetProps={widgetProps}
                        onEditModeChange={setLayoutEditState}
                        containerWidth={embedded ? undefined : 1100}
                    />
                )}

                {activeTab === 'analytics' && (
                  <Suspense fallback={<div className="p-8 text-gray-500">Loading analytics…</div>}>
                    <AnalyticsTab firmId={firmId} currentAum={firm.aum} currentEmployees={firm.totalEmployees} />
                  </Suspense>
                )}
                {activeTab === 'mandates' && <MandatesTab firmId={firmId} />}
                {activeTab === 'candidates' && <FirmCandidatesTab firmId={firmId} />}

              </div>
            </div>
        </div>
    );
}
